import { forEach, forOwn } from 'lodash';

import { Base, Magnitude, createPromise, resolve, clone, cloneDeep } from 'gear';

import { Matrix, Bounds, Boundary, curvesFromSvgPath } from '../../geometry';

let { opentype } = window;


const isLower = function(c) {
  return ((c >= 'a') && (c <= 'z'));
};
const isUpper = function(c) {
  return ((c >= 'A') && (c <= 'Z'));
};
const isAlpha = function(c) {
  return isLower(c) || isUpper(c);
};

const computedSlantedAdvance = function( aX, aY, lineDirection ) {
  const dX = Math.abs(lineDirection.x);
  const dY = Math.abs(lineDirection.y);
  const phi = Math.atan2(dY,dX);
  return dX >= dY ? aX / Math.cos(phi) : aY / Math.sin(phi);
};

export const Glyph = Base.extend({ typeName: 'Glyph',

  // [TODO] Use properties

  init( char, outlines, bounds, hPos, advanceX, advanceY, originalIdx ) {
    this._char     = char;
    this._outlines = outlines;
    this._bounds   = bounds;
    this._hPos     = hPos;
    this._advanceX = advanceX;
    this._advanceY = advanceY;
    this._originalIdx = originalIdx;
  },

  clone() {
    return new Glyph( this._char, clone(this._outlines), this._bounds, this._hPos, this._advanceX, this._advanceY, this._originalIdx ) ;
  },

  scaled( scale ) {
    const outlines = cloneDeep(this._outlines);
    const s = Matrix.scale(scale);
    if( outlines ) {
      forEach( outlines, o => o.transform(s) );
    }
    return new Glyph( this._char, outlines, this._bounds.scalingTransformed(s), this._hPos*scale, this._advanceX*scale, this._advanceY*scale, this._originalIdx ) ;
  },
});

// MissingGlyphsBehavior { Skip, UseSpace, UseSquare }

export const FontData = Base.extend({ typeName: 'FontData',

  Properties: {
    sizeReferenceChar:        { type: 'char'   , def: 'M'    },
    automaticCaseReplacement: { type: 'boolean', def: true   },
    missingGlyphsBehavior:    { type: 'string' , def: 'UseSquare' }
  },

  height      ( scale) { return this._height      * scale; },
  ascent      ( scale) { return this._ascent      * scale; },
  descent     ( scale) { return this._descent     * scale; },
  upperSize   ( scale) { return this._upperSize   * scale; },
  lineSpacing ( scale) { return this._lineSpacing * scale; },
  width       ( scale) { return this._width       * scale; },
  spaceWidth  ( scale) { return this._spaceWidth  * scale; },
  spaceHeight ( scale) { return this._spaceHeight * scale; },

  sizeOfReferenceChar( scale ) { return this._sizeOfReferenceChar * scale; },

  /*
  init(  fontInfo, requestedGlyphs ) {

    this._fontInfo = fontInfo;

    this._kTable = {};
    this._glyphs = {};
    this._glyphsString = "";

    this.parseInfo(fontInfo,requestedGlyphs);

    const tm = fontInfo.Metrics;

    this._referenceSize = tm.RequestedSize || tm.Size;

    // Parse metrics, we leave them at the reference size to be able to share the
    // same font for multiple font sizes. The desire size is passed as a parameter when
    // asked for the metrics

    this._ascent      = tm.Ascent ;
    this._descent     = tm.Descent;
    this._height      = tm.Height ;
    this._lineSpacing = tm.LineSpacing ;

    this._width       = tm.AverageWidth;

    // See http://www.emdpi.com/fontsize.html
    // Maybe we should find a better name for this property
    this._upperSize = tm.UpperSize;

    this._spaceWidth  = tm.SpaceWidth;
    this._spaceHeight = tm.SpaceHeight;

    this.initFontData();
  },
  */

  // Call after FontData internal properties have been initialized to
  // compute the sizeOfReferenceChar (used by autoSize=true and some processes)
  initFontData() {

    let sizeOfReferenceChar;
    const r = this.findSizeReferenceChar()
    if( r && r._outlines ) {
      sizeOfReferenceChar = Bounds.ofThings(r._outlines).height;
    }
    else {
      const ascent = this._ascent, height = this._height;
      if( ascent > 0 ) {
        sizeOfReferenceChar = ascent;
      }
      else if( height > 0 ) {
        sizeOfReferenceChar = height * 0.9;
      }
      else {
        sizeOfReferenceChar = this._referenceSize;
      }
    }
  
    this._sizeOfReferenceChar = sizeOfReferenceChar;
  },

  findSizeReferenceChar() {
    const glyphs = this._glyphs;

    const lSequenceToAttempt = [
      this._sizeReferenceChar, // M by default
      'M',
      '1',
      'A',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      'W',
      'I',
      't',
      'j',    
    ];

    for (let index = 0; index < lSequenceToAttempt.length; index++) {
      const lReferenceChar = lSequenceToAttempt[index];
      const r = glyphs[lReferenceChar];
      if( r && r._outlines ) {
        return r;
      }
    }

    return null;
  },

  // This is scale needs to be given to metrics and createGlyphs
  // so we can keep only one Font for all sizes and autoSize value
  scaleForSize( desiredSize, autoSize ) {

    const referenceSize = autoSize ? this._sizeOfReferenceChar : this._referenceSize ;

    return desiredSize / referenceSize;
  },

  getKerning( a, b ) {
    return this._kTable[ a + b ] || 0;
  },

  createGlyphs( text, scale, extraCharSpacing, extraWordSpacing, lineDirection, correctZeroAdvance ) {

    this.prepareGlyphs( text ); // For fonts that are fully cached in memory
    const r = [];
    // Find spacing magnitudes in milimeters
    const absoluteExtraCharSpacingX = Magnitude.absoluteMMValueFor(extraCharSpacing, this._spaceWidth  * scale),
          absoluteExtraCharSpacingY = Magnitude.absoluteMMValueFor(extraCharSpacing,this._spaceHeight * scale),
          absoluteExtraWordSpacing  = Magnitude.absoluteMMValueFor(extraWordSpacing,this._spaceWidth  * scale);

    const length = text.length;

    let hPos = 0,
        lastChar = ' ',
        prevWasSpace = false;

    for( let i = 0; i < length ; ++i ) {
      const char = text[i];
      const almostHorizontal = Math.abs(lineDirection.x) > 0.9;
      const isSpace = ( char === ' ' );

      const glyph = this.createGlyph(char,scale);
      if( glyph ) {

        if( i > 0 ) {

          if( almostHorizontal ) {
            const kerning = lineDirection.x > 0 ? this.getKerning(lastChar,char) : this.getKerning(char,lastChar);
            hPos += ( kerning * scale );
          }

          if( ! ( isSpace || prevWasSpace ) ) {
            hPos += computedSlantedAdvance(absoluteExtraCharSpacingX,absoluteExtraCharSpacingY,lineDirection);
          }
        }

        glyph._hPos = hPos;
        glyph._originalIdx = i+1;

        var ax = glyph._advanceX ;
        var ay = glyph._advanceY ;

        if ( correctZeroAdvance )
        {
          if ( ax == 0 )           
            ax = glyph._bounds.width * 1.05 ; 
          if ( ay == 0 )           
            ay = glyph._bounds.height * 1.05 ; 
        }

        let advance = computedSlantedAdvance(ax,ay,lineDirection);

        if( isSpace ) {
          advance += absoluteExtraWordSpacing;
          prevWasSpace = true;
        }
        else{
          prevWasSpace = false;
        }

        hPos += advance;

        r.push(glyph);

        lastChar = char;
      }
    }
    return r;
  },

  prepareGlyphs(/* chars */) {
    return null; // Default implementation
  },

  createGlyph( char, scale ) {

    const glyphs = this._glyphs;
    let glyph = glyphs[char];

    // A TTF might have glyphs which are well defined but have no outline. Spaces are the common example, but
    // a font having just numbers and/or upper case letters will have defined glyphs, with no outline, for the other letters
    if( !glyph || !glyph._outlines ) {

      if( isAlpha(char) ) {
        if( this._automaticCaseReplacement ) {
          const replacementChar = isLower(char) ? char.toUpperCase() : char.toLowerCase();

          glyph = glyphs[replacementChar];
        }
      }

      if( char !== ' ' && ( !glyph || !glyph._outlines ) ) {

        switch( this._missingGlyphsBehavior ) {
          case 'Skip':      return null;
          case 'UseSpace':  return glyph || this.createDefaultGlyph(char,null,scale);
          case 'UseSquare': return this.createDefaultGlyph(char,this.createRectangularOutline(scale),scale);
        }
      }
    }
    return glyph.scaled(scale);
  },

  createDefaultGlyph( char, outlines, scale ) {
    const width = this.width(scale);
    return new Glyph( char, outlines, Bounds(0,0,width,width), 0, width, width, 0 );
  },

  createRectangularOutline( scale) {
    const width = this.width(scale);
    const l = width * 0.1;
    const h = width * 0.9;
    const box = Boundary.createRectangle(new Bounds(l,l,h,h));
    return [ box ];
  }
});

// Opentype.js based FontData
// Useful resources:
// https://www.microsoft.com/typography/otspec/os2.htm
// http://fontforge.github.io/fontinfo.html

let LocalFontData = FontData.extend({ typeName: 'LocalFontData',

  init( otFont ) {

    this._otFont = otFont;

    // console.log(otFont);

    this._kTable = {};
    this._glyphs = {};
    this._glyphsString = "";

    this.parseKerning();

    // Parse needed glyphs for metrics
    this.parseGlyphs(' MAX');

    // tmSizeInPoints == otFont.unitsPerEm

    // Parse metrics, we leave them at the reference size to be able to share the
    // same font for multiple font sizes. The desire size is passed as a parameter when
    // asked for the metrics

    const { tables } = otFont;
    const { os2, hhea } = tables;

    const ascent  = this._ascent  = os2 ? os2.usWinAscent : hhea.ascender;
    const descent = this._descent = os2 ? os2.usWinDescent : ( -hhea.descender + hhea.lineGap );

    const height = this._height  = ascent + descent; // tm.Height ;
    this._lineSpacing = height;           // tm.LineSpacing ; In the engine these are equal

    const size = this._referenceSize = otFont.unitsPerEm;

    // See http://www.emdpi.com/fontsize.html
    // Maybe we should find a better name for this property
    this._upperSize = size - descent ; // ascent - internalLeading

    const space = this._glyphs[' '];
    this._spaceWidth  = space._advanceX; // tm.SpaceWidth;
    this._spaceHeight = space._advanceY; // tm.SpaceHeight;

    this._width = os2 ? os2.xAvgCharWidth : this._spaceWidth; // tm.AverageWidth;

    this.initFontData();
  },

  prepareGlyphs( chars ) {
    this.parseGlyphs( chars );
  },

  parseGlyphs( requestedGlyphs ) {
    //debugger;
    const otFont = this._otFont;

    const glyphs = this._glyphs;

    forEach(requestedGlyphs, c => {
      if( ! glyphs[c] ) {

        const otGlyph = otFont.charToGlyph(c);
        if( otGlyph ) {

          let aX = otGlyph.advanceWidth,
              aY = otGlyph.advanceHeight,
              outlines = null,
              bounds;

          const otPath = otGlyph.path;
          const otCommands = otPath.commands;

          if( otCommands.length > 0 ) {
            // Looks like there is a bug in opentype.js, we need to force
            // paths to be closed because there can be missing Z commands
            // Check the O in Varsity for an example
            outlines = curvesFromSvgPath(otCommands,{ forceClosed: true });
          }

          if( outlines && outlines.length > 0 ) {
            bounds = Bounds.ofThings(outlines);
          }
          else {
            outlines = null;
            bounds = Bounds(0,0,aX,aY);
          }


          glyphs[c] = new Glyph(c,outlines,bounds,0,aX,aY,0);
        }

        this._glyphsString += c;
      }
    });
  },

  parseKerning() {
    const kTable = this._kTable;

    const otFont = this._otFont;
    const otKern = otFont.kerningPairs;

    forOwn( otKern, (kern,ab) => {
      const a_b = ab.split(',');
      const a = otFont.glyphIndexToName(parseInt(a_b[0]));
      const b = otFont.glyphIndexToName(parseInt(a_b[1]));
      kTable[a+b] = parseFloat(kern);
    });
  }
});



const localFontDataStore = {};
const localFontDataStoreBySrc = {};
const localFontDataStorePromise = {};

FontData.localLoad$ = function( font, src ) {
  const t = font.textualize();

  let lf = localFontDataStore[t];

  // Collapse font data from the same source, even if they
  // come from different fonts
  if( ! lf ) {
    lf = localFontDataStoreBySrc[src];
    if( lf ) {
      localFontDataStore[t] = lf;
    }
  }

  if( lf ) {
    return resolve(lf);
  }

  // Check if we are already asking this file, and just
  // wait for it in that case
  let promise = localFontDataStorePromise[src];
  if( promise ) {
    return promise;
  }

  // Start a new request
  promise = localFontDataStorePromise[src] = createPromise( (resolve,reject) => {

    opentype.load( src, (err, font) => {
      if(err) {
        console.log(err.toString());
        return reject();
      }

      const lf = localFontDataStoreBySrc[src] =
                 localFontDataStore[t]        = new LocalFontData(font);

      // Remove the promise, we are done here
      delete localFontDataStorePromise[src];

      resolve(lf);
    });
  });
  return promise;
};



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/text/font-data.js
 **/